// Exact swept collision for the office body; shared by path verification and the mover.
import { BODY } from './grid.js';
const EPS = 1e-7;

/** Can the body slide in a straight line from position (ax, ay) to (bx, by) without overlapping a solid tile?
 *  Exact: each solid tile, grown by the body box, is an open rectangle the line must not pass through (touching an
 *  edge is free, as in collide.js boxFree). `margin`: keep at least that far from every tile as well. */
export function sweepClear(solid, ax, ay, bx, by, boat, margin = 0) {
  const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy);
  // the line in pieces of at most half a tile, each checked against the tiles its box can touch
  const n = Math.max(1, Math.ceil(len / 0.5));
  for (let k = 0; k < n; k++) {
    const px = ax + (dx * k) / n, py = ay + (dy * k) / n, qx = ax + (dx * (k + 1)) / n, qy = ay + (dy * (k + 1)) / n;
    const x0 = Math.floor(Math.min(px, qx) + BODY.l - margin), x1 = Math.floor(Math.max(px, qx) + BODY.r + margin), y0 = Math.floor(Math.min(py, qy) + BODY.t - margin), y1 = Math.floor(Math.max(py, qy) + BODY.b + margin);
    for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
      if (!solid(tx, ty, boat)) continue;
      // positions where the box overlaps tile (tx, ty): the open rectangle (tx - r, tx + 1 - l) x (ty - b, ty + 1 - t)
      if (segHitsBox(px, py, qx, qy, tx - BODY.r - margin, tx + 1 - BODY.l + margin, ty - BODY.b - margin, ty + 1 - BODY.t + margin)) return false;
    }
  }
  return true;
}
function segHitsBox(ax, ay, bx, by, X0, X1, Y0, Y1) {
  const dx = bx - ax, dy = by - ay;
  let t0 = 0, t1 = 1;
  if (Math.abs(dx) < 1e-12) { if (!(ax > X0 + EPS && ax < X1 - EPS)) return false; }
  else { let a = (X0 - ax) / dx, b = (X1 - ax) / dx; if (a > b) { const s = a; a = b; b = s; } if (a > t0) t0 = a; if (b < t1) t1 = b; }
  if (Math.abs(dy) < 1e-12) { if (!(ay > Y0 + EPS && ay < Y1 - EPS)) return false; }
  else { let a = (Y0 - ay) / dy, b = (Y1 - ay) / dy; if (a > b) { const s = a; a = b; b = s; } if (a > t0) t0 = a; if (b < t1) t1 = b; }
  const len = Math.hypot(dx, dy);
  return len < 1e-12 || (t1 - t0) * len > 1e-6; // (a point inside on both axes hits)
}

/** Does the body fit at (x, y)? (collide.js boxFree, from `solid` alone) */
export function boxFits(solid, x, y, boat) {
  const x0 = Math.floor(x + BODY.l), x1 = Math.floor(x + BODY.r - 1e-6), y0 = Math.floor(y + BODY.t), y1 = Math.floor(y + BODY.b - 1e-6);
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) if (solid(tx, ty, boat)) return false;
  return true;
}

// The overlap width on each axis is piecewise linear, so the overlap area is piecewise quadratic. Check its
// derivative at both ends of every piece: it must never increase, even when a diagonal moves toward one edge.
function overlapShrinks(ax, ay, dx, dy, tx, ty) {
  const cuts = [0, 1];
  for (const [origin, delta, lo, hi, p, q] of [[ax, dx, tx, tx + 1, BODY.l, BODY.r], [ay, dy, ty, ty + 1, BODY.t, BODY.b]]) {
    if (Math.abs(delta) < 1e-12) continue;
    for (const edge of [lo, hi]) for (const offset of [p, q]) {
      const t = (edge - offset - origin) / delta; if (t > 0 && t < 1) cuts.push(t);
    }
  }
  cuts.sort((a, b) => a - b);
  const width = (p, lo, hi, l, r) => Math.max(0, Math.min(p + r, hi) - Math.max(p + l, lo));
  for (let i = 1; i < cuts.length; i++) {
    const a = cuts[i - 1], b = cuts[i]; if (b - a < 1e-12) continue;
    const xa = width(ax + dx * a, tx, tx + 1, BODY.l, BODY.r), xb = width(ax + dx * b, tx, tx + 1, BODY.l, BODY.r);
    const ya = width(ay + dy * a, ty, ty + 1, BODY.t, BODY.b), yb = width(ay + dy * b, ty, ty + 1, BODY.t, BODY.b);
    const sx = (xb - xa) / (b - a), sy = (yb - ya) / (b - a);
    if (sx * ya + sy * xa > EPS || sx * yb + sy * xb > EPS) return false;
  }
  return true;
}

/** Can an already-overlapping body move toward free space without touching any new solid tile? Its feet must
 *  remain legal and the overlap area with each originally intersected tile must never increase. */
export function escapeClear(solid, ax, ay, bx, by, boat) {
  const dx = bx - ax, dy = by - ay;
  if (solid(Math.floor(ax + 0.5), Math.floor(ay + 0.8), boat) ||
      solid(Math.floor(bx + 0.5), Math.floor(by + 0.8), boat)) return false;
  const x0 = Math.floor(Math.min(ax, bx) + BODY.l), x1 = Math.floor(Math.max(ax, bx) + BODY.r);
  const y0 = Math.floor(Math.min(ay, by) + BODY.t), y1 = Math.floor(Math.max(ay, by) + BODY.b);
  let overlapping = false;
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
    if (!solid(tx, ty, boat)) continue;
    if (segHitsBox(ax + 0.5, ay + 0.8, bx + 0.5, by + 0.8, tx, tx + 1, ty, ty + 1)) return false;
    const initially = ax + BODY.r > tx + EPS && ax + BODY.l < tx + 1 - EPS &&
      ay + BODY.b > ty + EPS && ay + BODY.t < ty + 1 - EPS;
    if (initially) {
      overlapping = true;
      if (!overlapShrinks(ax, ay, dx, dy, tx, ty)) return false;
    } else if (segHitsBox(ax, ay, bx, by, tx - BODY.r, tx + 1 - BODY.l, ty - BODY.b, ty + 1 - BODY.t)) return false;
  }
  return overlapping;
}

/** Short straight escapes for a body overlapping a wall while its feet remain on a legal tile. */
export function recoveryPositions(solid, x, y, boat) {
  if (boxFits(solid, x, y, boat) || solid(Math.floor(x + 0.5), Math.floor(y + 0.8), boat)) return [];
  const out = [];
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    for (let i = 1; i <= 100; i++) {
      const px = dx ? +(x + dx * i * 0.01).toFixed(2) : x, py = dy ? +(y + dy * i * 0.01).toFixed(2) : y;
      if (boxFits(solid, px, py, boat)) {
        if (escapeClear(solid, x, y, px, py, boat)) out.push([px, py]);
        break;
      }
    }
  }
  return out.sort((a, b) => Math.hypot(a[0] - x, a[1] - y) - Math.hypot(b[0] - x, b[1] - y));
}
